import {watch} from 'vue'
import {signal} from '@/messages'
import {useAppStore, useMeetingStore, useUserStore, useSignalStore} from '@/stores'
import {useRepository} from '@/composables'
import {roundDecimals} from '@/utils'
import {mediator} from '@communication'
import {useEventBus, tryOnScopeDispose} from '@vueuse/core'

// The round-trip logic stays a plain class; useSignalCallback only wires it to the event bus and the stores
class SignalMonitor {
  [key: string]: any
  constructor() {
    this.user = useUserStore()
    this.signal = useSignalStore()
    this.breakpoints = [0.3, 0.75, 2]
    this.threshold = 100
    this.duration = 1000
    this.interval = undefined
    this.errorsAllowed = 5
    this.forceSignal = false
    this.timestamp = 0
    this.entries = []
    this.errors = 0
    this.checkConnection()
  }

  send = message => {
    mediator.broadcast(message)
  }

  // payload of 'signal:round:trip'
  handler = (data: any) => {
    const {start} = data
    if (this.user.isAssistant) {
      const end = Math.round(window.performance.now())
      const collation = end - start > this.threshold
      this.entries.push({
        start,
        end,
      })
      this.timestamp = 0
      this.aggregateErrors(collation)
    } else if (this.user.isClient) {
      this.send(signal.roundTripMessage(start))
    }
  }

  start = () => {
    if (!this.interval) {
      this.interval = setInterval(() => {
        if (this.timestamp > 0) {
          this.entries.push({
            start: this.timestamp,
            end: null,
          })
          this.aggregateErrors(true)
        }
        const start = Math.round(window.performance.now())
        this.send(signal.roundTripMessage(start))
        this.timestamp = start
      }, this.duration)
    }
  }

  stop = () => {
    this.interval = clearInterval(this.interval)
    this.calculateStats()
    this.timestamp = 0
    this.entries = []
    this.errors = 0
  }

  checkConnection = () => {
    const {connection} = navigator
    if (connection) {
      const processSignal = () => {
        const signal = this.getSignal(connection.downlink)
        this.setSignal(signal)
        this.user.setSignal(signal)
      }
      processSignal()
      connection.addEventListener('change', processSignal)
    }
  }

  getSignal = num => {
    const signals = [...this.breakpoints, ...(this.breakpoints.includes(num) ? [] : [num])].sort((a, b) => a - b)
    return signals.indexOf(num)
  }

  setSignal = level => {
    const [min] = this.breakpoints
    const warning = level < min
    this.signal.setLevel(level)
    this.signal.setWarning(warning)
    this.forceSignal = warning
  }

  aggregateErrors = fail => {
    this.errors = fail ? this.errors + 1 : 0
    const exceeded = this.forceSignal || this.errors > this.errorsAllowed
    this.signal.setWarning(exceeded)
  }

  calculateStats() {
    if (this.entries.length) {
      const {sendWsEvent} = useRepository()
      const {durations, lost} = this.entries.reduce(
        (obj, item) => {
          if (item.end) {
            obj.durations.push(item.end - item.start)
          } else {
            obj.lost.push(item.start)
          }
          return obj
        },
        {
          durations: [],
          lost: [],
        }
      )
      durations.sort((a, b) => a - b)
      const {0: min, length: l, [l - 1]: max} = durations
      let average = durations.reduce((a, b) => a + b, 0) / this.entries.length
      average = roundDecimals(average, 2)
      const total = lost.length
      const percent = Math.round((total / this.entries.length) * 10000) / 100
      sendWsEvent(
        signal.roundTripTime({
          min,
          max,
          average,
          lost: {
            total,
            percent,
            timestamps: lost,
          },
        })
      )
    }
  }
}

export const useSignalCallback = () => {
  const monitor = new SignalMonitor()
  const app = useAppStore()
  const meeting = useMeetingStore()
  const user = useUserStore()

  useEventBus('signal:round:trip').on(monitor.handler)
  // The monitor runs from the start of the call until the lobby or the exit
  const stopWatchView = watch(
    () => meeting.view,
    (view, oldView) => {
      if (!user.isAssistant) return
      if (view === 'call' && oldView === 'lobby') {
        monitor.start()
      } else if (view === 'lobby') {
        monitor.stop()
      }
    },
    {flush: 'sync'}
  )
  const stopWatchEnd = watch(
    () => app.isEnd,
    isEnd => {
      if (isEnd && user.isAssistant) monitor.stop()
    },
    {flush: 'sync'}
  )
  watch(
    () => user.isAssistant,
    val => {
      if (!meeting.isLobby) {
        if (val) {
          monitor.start()
        } else {
          monitor.stop()
        }
      }
    }
  )
  tryOnScopeDispose(() => {
    stopWatchView()
    stopWatchEnd()
  })
}
